'use strict';

/**
 * Thumbnails — horizontal strip showing every page at small scale, with a
 * markup-count badge per page and an active highlight. Renders via pdfjs-dist
 * the same way PdfCanvas does but at a fixed thumbnail width.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const THUMB_WIDTH = 90;

export default function Thumbnails({ pdfUrl, pageCount, currentPage, onPick, markups = [] }) {
  const countsByPage = useMemo(() => {
    const m = new Map();
    markups.forEach((mk) => m.set(mk.page, (m.get(mk.page) || 0) + 1));
    return m;
  }, [markups]);

  if (!pageCount || pageCount < 1) return null;

  return (
    <div className="bg-slate-100 border-t border-slate-200 overflow-x-auto whitespace-nowrap py-2 px-2">
      <div className="inline-flex gap-2">
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
          <ThumbItem
            key={p}
            pdfUrl={pdfUrl}
            page={p}
            count={countsByPage.get(p) || 0}
            active={p === currentPage}
            onClick={() => onPick?.(p)}
          />
        ))}
      </div>
    </div>
  );
}

function ThumbItem({ pdfUrl, page, count, active, onClick }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const doc = await pdfjsLib.getDocument({ url: pdfUrl, withCredentials: true }).promise;
        if (cancelled) return;
        const pg = await doc.getPage(page);
        const baseVp = pg.getViewport({ scale: 1 });
        const scale = THUMB_WIDTH / baseVp.width;
        const vp = pg.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.floor(vp.width * dpr);
        canvas.height = Math.floor(vp.height * dpr);
        canvas.style.width = `${vp.width}px`;
        canvas.style.height = `${vp.height}px`;
        const ctx = canvas.getContext('2d');
        await pg.render({
          canvasContext: ctx,
          viewport: vp,
          transform: dpr === 1 ? null : [dpr, 0, 0, dpr, 0, 0],
        }).promise;
      } catch (err) {
        if (!cancelled && err?.name !== 'RenderingCancelledException') {
          console.warn('[thumb] render failed for page', page, ':', err.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [pdfUrl, page]);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative inline-flex flex-col items-center p-1 rounded
        ${active ? 'ring-2 ring-blue-500 bg-white' : 'hover:bg-slate-200 bg-slate-50'}`}
      title={`Page ${page}${count ? ` · ${count} markup${count === 1 ? '' : 's'}` : ''}`}
    >
      <canvas ref={canvasRef} className="block bg-white" />
      <div className="text-[10px] text-slate-600 mt-0.5 flex items-center gap-1">
        <span>{page}</span>
        {count > 0 && (
          <span className="bg-amber-500 text-white rounded-full px-1.5 text-[9px] font-bold leading-tight">
            {count}
          </span>
        )}
      </div>
    </button>
  );
}
